import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { InteractionStatus } from '@azure/msal-browser';
import { DividerModule } from 'primeng/divider';
import { filter, switchMap, take, timeout, timer } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  clearSetPasswordFlow,
  getSetPasswordToken,
  hasPendingB2cRedirect,
  hasPendingMsalInteraction,
  isRedirectHandleDone,
  isSetPasswordFlow,
  isSetPasswordFlowForToken,
  markSetPasswordAttempted,
  markSetPasswordFlow,
  msalConfig,
  wasSetPasswordAttempted,
} from '../../auth.config';
import { FirmService } from '../../services/firmservice';

@Component({
  selector: 'app-setpassword',
  standalone: true,
  imports: [CommonModule, FormsModule, DividerModule],
  templateUrl: './setpassword.component.html',
  styleUrl: './setpassword.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SetpasswordComponent implements OnInit {

  token: string = '';
  roleType: string = '';
  firmLogoPath: string = '';
  firmName: string = '';

  circle2On: boolean = false;

  constructor(
    private route: ActivatedRoute,
    private changeDetectorRef: ChangeDetectorRef,
    private firmService: FirmService,
    private msalService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private router: Router,
  ) {}
  ngOnInit(): void {
    setTimeout(() => {
      this.circle2On = true;
      this.changeDetectorRef.markForCheck();
    }, 1500);
    this.route.paramMap.subscribe((params) => {
      this.token = params.get('token') || '';
      const routeRoleType = params.get('roleType') || '';

      if (this.token.length === 0) {
        return;
      }

      // Wait for MSAL initialize + handleRedirect to fully finish (avoids race).
      this.whenMsalReady(() => this.beginSetPasswordFlow(routeRoleType));
    });
    this.changeDetectorRef.detectChanges();
    console.info('token ::::', this.token);





  }

  private whenMsalReady(onReady: () => void): void {
    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status) => status === InteractionStatus.None),
        take(1),
        switchMap(() => {
          if (isRedirectHandleDone()) {
            return timer(0);
          }
          return timer(0, 50).pipe(
            filter(() => isRedirectHandleDone()),
            take(1),
            timeout({ first: 8000, with: () => timer(0) })
          );
        })
      )
      .subscribe(() => onReady());
  }

  private beginSetPasswordFlow(routeRoleType: string): void {
    if (hasPendingB2cRedirect()) {
      return;
    }

    if (wasSetPasswordAttempted(this.token)) {
      this.redirectToFirmLogin();
      return;
    }

    // Returned from B2C reset (confirmed page) without OAuth hash — go to login.
    if (isSetPasswordFlowForToken(this.token)) {
      clearSetPasswordFlow();
      markSetPasswordAttempted(this.token);
      this.redirectToFirmLogin();
      return;
    }

    if (isSetPasswordFlow()) {
      clearSetPasswordFlow();
    }

    this.loadUserAndStartPasswordReset(routeRoleType);
  }

  private loadUserAndStartPasswordReset(routeRoleType: string): void {
    const userId = this.token;
    this.firmService.getUserProfileResetPasswordByAzureuserId(userId).subscribe((data: any) => {
      console.info('OUTPUT getFirmDetailsByName  ' + JSON.stringify(data));
      const userRoles = data.UserRoles as Array<{ UserRole?: string }> | undefined;
      const fromUserRoles =
        Array.isArray(userRoles) && userRoles.length > 0
          ? userRoles[0]?.UserRole || ''
          : '';
      this.firmName = data.firmName;
      this.roleType =
        fromUserRoles || (data.user_role as string) || routeRoleType;
      if (this.roleType) {
        localStorage.setItem('roleType', this.roleType);
      } else {
        localStorage.removeItem('roleType');
      }

      if (wasSetPasswordAttempted(this.token) || isSetPasswordFlowForToken(this.token)) {
        this.redirectToFirmLogin();
        return;
      }

      const profile =
        data.last_login != null && data.last_login != ''
          ? data.profile_1
          : data.profile_2;
      console.info('profile ' + profile);

      if (data.logoURL) {
        this.firmLogoPath = data.logoURL;
      } else {
        console.info('Default Logo Setting');
        this.firmLogoPath =
          'https://fispokestorageaccdev.blob.core.windows.net/firmlevelrepo/FirmAzureAdTemplates/parentfirm03/images/logo.png';
      }
      localStorage.setItem('LOGO', this.firmLogoPath);

      if (profile) {
        localStorage.setItem('FIRMID', data.FirmId);
        localStorage.setItem('sso_button_name', data.sso_button_name);
        localStorage.setItem('firmname', this.firmName);
        const isAdvisor =
          this.roleType?.trim() === 'advisor' &&
          (data.last_login != null || data.last_login != '');
        this.updateMsalAuthority(profile, isAdvisor);
      } else {
        console.error('Invalid Authority URL:', profile);
      }
      this.changeDetectorRef.markForCheck();
    });
  }

  updateMsalAuthority(policyName: string, isAdvisor: boolean) {
    if (!policyName) {
      console.error('Policy name is missing. Cannot update authority.');
      return;
    }

    const tenantName = environment.mslConfig.tenantName;
    const newAuthority = `https://${tenantName}.b2clogin.com/${tenantName}.onmicrosoft.com/${policyName}`;
    const sso_button_name = localStorage.getItem('sso_button_name');

    if (!this.isValidUrl(newAuthority)) {
      console.error('Invalid constructed authority URL:', newAuthority);
      return;
    }

    const startLogin = () => {
      if (hasPendingB2cRedirect()) {
        return;
      }
      if (wasSetPasswordAttempted(this.token) || isSetPasswordFlowForToken(this.token)) {
        this.redirectToFirmLogin();
        return;
      }

      markSetPasswordFlow(policyName, this.token);

      msalConfig.auth.authority = newAuthority;
      msalConfig.auth.redirectUri = environment.mslConfig.redirectUri;
      msalConfig.auth.knownAuthorities = [tenantName + '.b2clogin.com'];

      if (isAdvisor) {
        localStorage.setItem('customParam2', 'adv');
        localStorage.setItem('firmname', this.firmName);
        localStorage.setItem('customParam3', sso_button_name || '');
      }

      this.msalService.loginRedirect({
        authority: newAuthority,
        scopes: [
          'openid',
          'profile',
          'offline_access',
          'https://' +
            environment.mslConfig.tenantName +
            '.onmicrosoft.com/' +
            environment.mslConfig.clientId +
            '/read',
        ],
        extraQueryParameters: {
          customParam1: window.btoa(this.firmLogoPath),
          token: this.token,
          customParam2: isAdvisor ? 'adv' : 'client',
          firmname: this.firmName,
          customParam3: sso_button_name || '',
        },
      });
    };

    if (hasPendingMsalInteraction()) {
      this.msalBroadcastService.inProgress$
        .pipe(
          filter((status) => status === InteractionStatus.None),
          take(1)
        )
        .subscribe(() => startLogin());
      return;
    }

    startLogin();
  }

  private redirectToFirmLogin(): void {
    const firm = this.firmName || localStorage.getItem('firmname') || 'fispoke';
    const roleType = this.roleType || localStorage.getItem('roleType') || '';
    const path =
      roleType === 'adv' ? `/firmlogin/${firm}/adv` : `/firmlogin/${firm}`;
    this.router.navigate([path]);
  }

  // ✅ Helper function to validate URL
  isValidUrl(url: string): boolean {
    try {
      new URL(url); // Will throw an error if not a valid URL
      return true;
    } catch (e) {
      console.error("Error : " + e);
      return false;
    }
  }
}
